import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi } from '../api';
import {
  CUSTOMER_STATUSES, CUSTOMER_STATUS_COLORS,
  CUSTOMER_TYPES, CUSTOMER_TYPE_COLORS,
  REFERRAL_SOURCES, REFERRAL_SOURCE_DETAILS,
  DATE_CRITERIA, SEARCH_CRITERIA,
} from '../constants';

interface PhotoCustomer {
  photoCustomerIdx: number;
  groomName?: string;
  groomPhone?: string;
  brideName?: string;
  bridePhone?: string;
  email?: string;
  customerType?: string;
  customerStatus: string;
  rehearsalDt?: string;
  mainShootingDt?: string;
  selectDt?: string;
  weddingDt?: string;
  weddingVenue?: string;
  orderDt?: string;
  partnerVendorName?: string;
  partnerStaffName?: string;
  rehearsalPhotographer?: { userIdx: number; name: string };
  mainPhotographer?: { userIdx: number; name: string };
  assignedStaff?: { userIdx: number; name: string };
  purchaseItems?: any[];
  totalContractAmount?: number;
  totalPaidAmount?: number;
  remainingAmount?: number;
  referralSource?: string;
  referralSourceDetail?: string;
  memo?: string;
  createDt: string;
}

export default function PhotoStudioCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<PhotoCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCriteria, setSearchCriteria] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateCriteria, setDateCriteria] = useState('createDt');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [photographerSearch, setPhotographerSearch] = useState('');
  const [photographerType, setPhotographerType] = useState<'all' | 'rehearsal' | 'main'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'basic' | 'shooting' | 'selection'>('basic');

  // 엑셀 import 상태
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<{
    customers: any[];
    globalWarnings: string[];
  } | null>(null);
  const [importSelection, setImportSelection] = useState<boolean[]>([]);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number; failed: string[] } | null>(null);
  const [editingRowIdx, setEditingRowIdx] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).then(res => res.json()).then(json => setStaffList(json.data?.items || json.items || [])).catch(() => {});
  }, []);

  const initialForm = {
    groomName: '', groomPhone: '', groomEmail: '',
    brideName: '', bridePhone: '', brideEmail: '',
    customerType: '', customerChannel: '' as 'VENDOR' | 'WALKING' | '',
    customerStatus: '가계약',
    rehearsalDt: '', mainShootingDt: '', selectDt: '', weddingDt: '', weddingVenue: '',
    rehearsalPhotographerIdx: '' as any, rehearsalPhotographer2Idx: '' as any,
    mainPhotographerIdx: '' as any, mainPhotographer2Idx: '' as any,
    // 업체 메타
    partnerVendorName: '', partnerStaffName: '', partnerStaffPhone: '',
    partnerServiceNote: '', overseasNote: '',
    // 본식 메타
    makeupShopName: '', makeupShopDt: '', mainMemo: '',
    // 발주(계약)일
    orderDt: '',
    // 셀렉 정보
    originalCdShipDt: '', shipMethod: '' as 'PICKUP' | 'COURIER' | '',
    courierPayment: '' as 'PREPAID' | 'COLLECT' | '',
    selectMethod: '' as 'VISIT' | 'EMAIL' | 'AUTO' | '',
    selectContent: '' as 'DIRECT' | 'AUTO' | '',
    selectManagerIdx: '' as any, selectMemo: '',

    notificationSettings: {
      rehearsalReminder: { oneMonth: false, oneWeek: false, oneDay: false },
      mainShootingReminder: { oneMonth: false, oneWeek: false, oneDay: false },
      selectReminder: { oneMonth: false, oneWeek: false, oneDay: false },
    },
    referralSource: '', referralSourceDetail: '', memo: '',
  };
  const [form, setForm] = useState(initialForm);
  const [channelFilter, setChannelFilter] = useState<'' | 'VENDOR' | 'WALKING'>('');

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await customerApi.list({
        q: searchQuery, searchCriteria, customerStatus: statusFilter, customerType: typeFilter,
        customerChannel: channelFilter || undefined,
        dateCriteria, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
        vendorName: vendorName || undefined, photographer: photographerSearch || undefined,
        photographerType: photographerType === 'all' ? undefined : photographerType,
        page, size: 20,
      });
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('고객 목록 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, searchCriteria, statusFilter, typeFilter, channelFilter, dateCriteria, dateFrom, dateTo, vendorName, photographerSearch, photographerType, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // 엑셀 import: LLM이 반환한 {basic, shooting, selection} 구조를 flat form state로 병합
  const mergeMappedIntoForm = (mapped: any) => {
    const b = mapped?.basic || {};
    const s = mapped?.shooting || {};
    const sel = mapped?.selection || {};
    const clean = (v: any) => (v === null || v === undefined ? '' : v);
    return {
      ...initialForm,
      groomName: clean(b.groomName), groomPhone: clean(b.groomPhone), groomEmail: clean(b.groomEmail),
      brideName: clean(b.brideName), bridePhone: clean(b.bridePhone), brideEmail: clean(b.brideEmail),
      customerChannel: clean(b.customerChannel), customerStatus: clean(b.customerStatus) || '가계약',
      partnerVendorName: clean(b.partnerVendorName), partnerStaffName: clean(b.partnerStaffName),
      partnerStaffPhone: clean(b.partnerStaffPhone), partnerServiceNote: clean(b.partnerServiceNote),
      overseasNote: clean(b.overseasNote),
      referralSource: clean(b.referralSource), referralSourceDetail: clean(b.referralSourceDetail),
      memo: clean(b.memo),
      customerType: clean(s.customerType), weddingVenue: clean(s.weddingVenue),
      makeupShopName: clean(s.makeupShopName), makeupShopDt: clean(s.makeupShopDt),
      mainMemo: clean(s.mainMemo), orderDt: clean(s.orderDt),
      rehearsalDt: clean(s.rehearsalDt), mainShootingDt: clean(s.mainShootingDt),
      selectDt: clean(s.selectDt), weddingDt: clean(s.weddingDt),
      originalCdShipDt: clean(sel.originalCdShipDt), shipMethod: clean(sel.shipMethod),
      courierPayment: clean(sel.courierPayment), selectMethod: clean(sel.selectMethod),
      selectContent: clean(sel.selectContent), selectMemo: clean(sel.selectMemo),
    };
  };

  // flat form state → {basic, shooting, selection} 구조로 역변환 (프리뷰 행에 반영)
  const formToMapped = (f: typeof initialForm, warnings: string[]) => ({
    basic: {
      customerChannel: f.customerChannel || null,
      customerStatus: f.customerStatus || null,
      groomName: f.groomName || null, groomPhone: f.groomPhone || null, groomEmail: f.groomEmail || null,
      brideName: f.brideName || null, bridePhone: f.bridePhone || null, brideEmail: f.brideEmail || null,
      partnerVendorName: f.partnerVendorName || null, partnerStaffName: f.partnerStaffName || null,
      partnerStaffPhone: f.partnerStaffPhone || null, partnerServiceNote: f.partnerServiceNote || null,
      overseasNote: f.overseasNote || null,
      referralSource: f.referralSource || null, referralSourceDetail: f.referralSourceDetail || null,
      memo: f.memo || null,
    },
    shooting: {
      customerType: f.customerType || null, weddingVenue: f.weddingVenue || null,
      makeupShopName: f.makeupShopName || null, makeupShopDt: f.makeupShopDt || null,
      mainMemo: f.mainMemo || null, orderDt: f.orderDt || null,
      rehearsalDt: f.rehearsalDt || null, mainShootingDt: f.mainShootingDt || null,
      selectDt: f.selectDt || null, weddingDt: f.weddingDt || null,
    },
    selection: {
      originalCdShipDt: f.originalCdShipDt || null, shipMethod: f.shipMethod || null,
      courierPayment: f.courierPayment || null, selectMethod: f.selectMethod || null,
      selectContent: f.selectContent || null, selectMemo: f.selectMemo || null,
    },
    warnings,
  });

  const handleEditPreviewRow = (i: number) => {
    if (!importPreview) return;
    setForm(mergeMappedIntoForm(importPreview.customers[i]));
    setEditingRowIdx(i);
    setActiveTab('basic');
    setShowAddModal(true);
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const result: any = await customerApi.importExcel(file);
      if (!result?.customers?.length) {
        alert(result?.globalWarnings?.join('\n') || '엑셀에서 고객 정보를 찾지 못했습니다.');
        return;
      }
      if (result.customers.length === 1) {
        setForm(mergeMappedIntoForm(result.customers[0]));
        setActiveTab('basic');
        setShowAddModal(true);
        const warnings = [...(result.globalWarnings || []), ...(result.customers[0].warnings || [])];
        if (warnings.length) alert(`매핑 완료. 확인 필요:\n- ${warnings.join('\n- ')}`);
      } else {
        setImportPreview({ customers: result.customers, globalWarnings: result.globalWarnings || [] });
        setImportSelection(result.customers.map(() => true));
      }
    } catch (err: any) {
      alert(`엑셀 업로드 실패: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleBulkCreateFromPreview = async () => {
    if (!importPreview) return;
    const rows = importPreview.customers.filter((_, i) => importSelection[i]);
    if (!rows.length) { alert('저장할 행을 선택해주세요.'); return; }
    setImportProgress({ done: 0, total: rows.length, failed: [] });
    const failed: string[] = [];
    for (let i = 0; i < rows.length; i++) {
      const merged = mergeMappedIntoForm(rows[i]);
      if (!merged.groomName && !merged.brideName) { failed.push(`${i + 1}행: 이름 누락`); setImportProgress(p => p && { ...p, done: p.done + 1, failed }); continue; }
      if (!merged.groomPhone && !merged.bridePhone) { failed.push(`${i + 1}행: 연락처 누락`); setImportProgress(p => p && { ...p, done: p.done + 1, failed }); continue; }
      try {
        const payload: any = { ...merged };
        const fk = ['rehearsalPhotographerIdx', 'rehearsalPhotographer2Idx', 'mainPhotographerIdx', 'mainPhotographer2Idx', 'selectManagerIdx'];
        for (const f of fk) if (!payload[f]) delete payload[f];
        const enums = ['customerChannel', 'shipMethod', 'courierPayment', 'selectMethod', 'selectContent', 'customerType'];
        for (const f of enums) if (!payload[f]) delete payload[f];
        if (!payload.groomEmail) delete payload.groomEmail;
        if (!payload.brideEmail) delete payload.brideEmail;
        await customerApi.create(payload);
      } catch (err: any) {
        failed.push(`${i + 1}행: ${err.message}`);
      }
      setImportProgress(p => p && { ...p, done: p.done + 1, failed });
    }
    await fetchCustomers();
    alert(`${rows.length - failed.length}건 등록 완료${failed.length ? `, ${failed.length}건 실패` : ''}.${failed.length ? '\n' + failed.join('\n') : ''}`);
    setImportPreview(null);
    setImportSelection([]);
    setImportProgress(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.groomName && !form.brideName) { alert('신랑 또는 신부 이름은 필수입니다.'); return; }
    if (!form.groomPhone && !form.bridePhone) { alert('신랑 또는 신부 연락처는 필수입니다.'); return; }

    // 프리뷰 행 편집 모드: DB 저장 없이 프리뷰 상태에만 반영
    if (editingRowIdx !== null && importPreview) {
      const existing = importPreview.customers[editingRowIdx];
      const updated = formToMapped(form, existing?.warnings || []);
      const nextCustomers = [...importPreview.customers];
      nextCustomers[editingRowIdx] = updated;
      setImportPreview({ ...importPreview, customers: nextCustomers });
      setShowAddModal(false);
      setForm(initialForm);
      setEditingRowIdx(null);
      return;
    }

    try {
      const cleanForm: any = { ...form };
      // 숫자 FK 필드: 비어있으면 payload 에서 제거
      const fkFields = [
        'rehearsalPhotographerIdx', 'rehearsalPhotographer2Idx',
        'mainPhotographerIdx', 'mainPhotographer2Idx',
        'selectManagerIdx',
      ];
      for (const f of fkFields) if (!cleanForm[f]) delete cleanForm[f];
      // enum/문자 필드: 빈 문자열이면 제거 (서버에서 ValidateIf 로 걸러짐)
      const enumFields = ['customerChannel', 'shipMethod', 'courierPayment', 'selectMethod', 'selectContent'];
      for (const f of enumFields) if (!cleanForm[f]) delete cleanForm[f];
      // 이메일 필드 정리
      if (!cleanForm.groomEmail) delete cleanForm.groomEmail;
      if (!cleanForm.brideEmail) delete cleanForm.brideEmail;

      await customerApi.create(cleanForm);

      alert('고객이 등록되었습니다.');
      setShowAddModal(false);
      setForm(initialForm);
      fetchCustomers();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  const formatDate = (dt?: string) => {
    if (!dt) return '미정';
    return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const daysUntil = (dt?: string) => {
    if (!dt) return null;
    const diff = Math.ceil((new Date(dt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return null;
    return diff;
  };

  const totalPages = Math.ceil(total / 20);
  const referralDetailOptions = REFERRAL_SOURCE_DETAILS[form.referralSource] || [];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객관리</h1>
            <p className="text-sm text-gray-500 mt-1">스튜디오 촬영 고객을 관리합니다</p>
          </div>
          <div className="flex items-center gap-2">
            <input id="excel-upload" type="file" accept=".xlsx,.xls,.csv" onChange={handleImportExcel} className="hidden" />
            <label htmlFor="excel-upload"
              className={`px-5 py-2 rounded-lg border border-gray-300 text-sm font-medium cursor-pointer shadow-sm flex items-center gap-1.5 transition-colors ${importing ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
              <i className={`ri-file-excel-2-line text-base ${importing ? '' : 'text-green-600'}`}></i>
              {importing ? '분석 중...' : '엑셀로 등록'}
            </label>
            <button onClick={() => { setActiveTab('basic'); setShowAddModal(true); }}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-sm font-medium cursor-pointer shadow-sm">
              <i className="ri-add-line text-base"></i> 고객 등록
            </button>
          </div>
        </div>

        {/* 필터 영역 */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          {/* 1행: 핵심 필터 */}
          <div className="flex items-center gap-3 flex-wrap">
            <select value={channelFilter} onChange={(e) => { setChannelFilter(e.target.value as any); setPage(1); }}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 min-w-[110px]">
              <option value="">고객 전체</option>
              <option value="VENDOR">업체고객</option>
              <option value="WALKING">워킹고객</option>
            </select>
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 min-w-[110px]">
              <option value="">구분 전체</option>
              {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 min-w-[110px]">
              <option value="">상태 전체</option>
              {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="text" placeholder="업체명" value={vendorName}
              onChange={(e) => { setVendorName(e.target.value); setPage(1); }}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[130px]" />
            <div className="h-5 w-px bg-gray-200" />
            <div className="flex items-center gap-1.5">
              {(['all', 'rehearsal', 'main'] as const).map(t => (
                <button key={t} onClick={() => { setPhotographerType(t); setPage(1); }}
                  className={`h-10 px-3 rounded-lg text-sm font-medium cursor-pointer transition-colors ${photographerType === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {t === 'all' ? '전체' : t === 'rehearsal' ? '리허설' : '본식'}
                </button>
              ))}
              <select value={photographerSearch} onChange={(e) => { setPhotographerSearch(e.target.value); setPage(1); }}
                className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="">촬영자 전체</option>
                {staffList.map((s: any) => <option key={s.userIdx} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          </div>
          {/* 2행: 기간 + 검색 */}
          <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              {DATE_CRITERIA.map(c => (
                <button key={c.value} onClick={() => { setDateCriteria(c.value); setPage(1); }}
                  className={`h-9 px-3 rounded-md text-xs font-medium cursor-pointer transition-colors ${dateCriteria === c.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} onBlur={() => setPage(1)}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[160px]" />
            <span className="text-gray-400 text-sm">~</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} onBlur={() => setPage(1)}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[160px]" />
            <div className="h-5 w-px bg-gray-200" />
            <select value={searchCriteria} onChange={(e) => { setSearchCriteria(e.target.value); setPage(1); }}
              className="h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50">
              {SEARCH_CRITERIA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <div className="relative flex-1 min-w-[180px]">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input type="text" placeholder="검색어 입력" value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="text-sm text-gray-500 whitespace-nowrap">
              총 <span className="font-semibold text-gray-700">{total}</span>건
            </div>
          </div>
        </div>

        {/* 고객 목록 테이블 */}
        {isLoading ? (
          <div className="flex justify-center p-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-20 text-center text-gray-400">
            <i className="ri-camera-line text-4xl block mb-2"></i>
            <p className="text-sm">등록된 고객이 없습니다</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-semibold text-gray-500 whitespace-nowrap">고객</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-500 whitespace-nowrap">업체</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-500 whitespace-nowrap">구분</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-500 whitespace-nowrap">상태</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-500 whitespace-nowrap">촬영자</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-500 whitespace-nowrap">리허설</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-500 whitespace-nowrap">셀렉</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-500 whitespace-nowrap">본식</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-500 whitespace-nowrap">납품물</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => {
                    const weddingDays = daysUntil(c.weddingDt);
                    return (
                      <tr key={c.photoCustomerIdx}
                        className="border-b border-gray-100 last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/photo-studio/customer-detail?id=${c.photoCustomerIdx}`)}>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col gap-0.5">
                            {c.groomName && <span className="font-medium text-gray-800"><i className="ri-men-line text-blue-400 mr-1"></i>{c.groomName}</span>}
                            {c.brideName && <span className="font-medium text-gray-800"><i className="ri-women-line text-pink-400 mr-1"></i>{c.brideName}</span>}
                            <span className="text-xs text-gray-400 mt-0.5">{formatDate(c.createDt)} 등록</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-gray-600">
                          {c.partnerVendorName || (c.referralSource === '웨딩플래너' ? c.referralSourceDetail || '플래너' : <span className="text-gray-300">-</span>)}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          {c.customerType && (
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${CUSTOMER_TYPE_COLORS[c.customerType] || 'bg-gray-100 text-gray-700'}`}>
                              {CUSTOMER_TYPES.find(t => t.value === c.customerType)?.label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${CUSTOMER_STATUS_COLORS[c.customerStatus] || 'bg-gray-100 text-gray-700'}`}>
                            {c.customerStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-gray-600">
                          <div className="flex flex-col gap-0.5">
                            {c.rehearsalPhotographer?.name && <span><span className="text-xs text-gray-400 mr-1">R</span>{c.rehearsalPhotographer.name}</span>}
                            {c.mainPhotographer?.name && <span><span className="text-xs text-gray-400 mr-1">M</span>{c.mainPhotographer.name}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-center text-gray-600">{formatDate(c.rehearsalDt)}</td>
                        <td className="px-4 py-3.5 text-center text-gray-600">{formatDate(c.selectDt)}</td>
                        <td className="px-4 py-3.5 text-center">
                          <span className="text-gray-700 font-medium">{formatDate(c.mainShootingDt)}</span>
                          {weddingDays !== null && (
                            <span className={`ml-1.5 text-xs font-bold ${weddingDays <= 14 ? 'text-red-500' : weddingDays <= 30 ? 'text-orange-500' : 'text-gray-400'}`}>
                              D-{weddingDays}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 flex-wrap max-w-[220px]">
                            {(c.purchaseItems || []).slice(0, 3).map((item: any, idx: number) => (
                              <span key={idx} className={`inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded ${
                                item.workStatus && item.workStatus !== '미배정'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {item.productName}
                              </span>
                            ))}
                            {(c.purchaseItems || []).length > 3 && (
                              <span className="text-xs text-gray-400">+{(c.purchaseItems || []).length - 3}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm cursor-pointer ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50 cursor-pointer">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* 고객 등록 모달 */}
        {showAddModal && (
          <div className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 ${editingRowIdx !== null ? 'z-[60]' : 'z-50'}`}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingRowIdx !== null ? `엑셀 매핑 #${editingRowIdx + 1} 수정` : '신규 고객 등록'}
                </h2>
                <button onClick={() => { setShowAddModal(false); setEditingRowIdx(null); setForm(initialForm); }} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              {/* 탭 네비게이션 */}
              <div className="sticky top-[57px] bg-white border-b border-gray-200 px-6 flex items-center gap-1 z-10">
                {([
                  { key: 'basic', label: '기본정보' },
                  { key: 'shooting', label: '일정·촬영' },
                  { key: 'selection', label: '셀렉·출고' },
                ] as const).map(t => (
                  <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                    className={`px-4 py-3 text-sm font-medium cursor-pointer border-b-2 transition-colors ${activeTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-6">
                {activeTab === 'basic' && (<>
                {/* 고객 구분 (업체/워킹) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객구분</label>
                    <select value={form.customerChannel}
                      onChange={(e) => setForm({ ...form, customerChannel: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">선택</option>
                      <option value="VENDOR">업체고객</option>
                      <option value="WALKING">워킹고객</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                    <select value={form.customerStatus} onChange={(e) => setForm({ ...form, customerStatus: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* 신랑 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-men-line text-blue-500"></i> 신랑 정보
                  </h3>
                  <p className="text-xs text-gray-400 mb-2 -mt-2">신랑 또는 신부 중 한 명의 이름과 연락처는 필수입니다</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑 이름 <span className="text-red-500">*</span></label>
                      <input type="text" value={form.groomName} onChange={(e) => setForm({ ...form, groomName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑 연락처 <span className="text-red-500">*</span></label>
                      <input type="tel" value={form.groomPhone} onChange={(e) => setForm({ ...form, groomPhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">신랑 이메일</label>
                      <input type="email" value={form.groomEmail} onChange={(e) => setForm({ ...form, groomEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
                {/* 신부 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-women-line text-pink-500"></i> 신부 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부 이름 <span className="text-red-500">*</span></label>
                      <input type="text" value={form.brideName} onChange={(e) => setForm({ ...form, brideName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부 연락처 <span className="text-red-500">*</span></label>
                      <input type="tel" value={form.bridePhone} onChange={(e) => setForm({ ...form, bridePhone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">신부 이메일</label>
                      <input type="email" value={form.brideEmail} onChange={(e) => setForm({ ...form, brideEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>

                {/* 업체(파트너) 정보 - 업체고객일 때만 노출 */}
                {form.customerChannel === 'VENDOR' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="ri-store-2-line text-gray-400"></i> 업체 정보
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">업체명</label>
                        <input type="text" value={form.partnerVendorName} onChange={(e) => setForm({ ...form, partnerVendorName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">업체 담당자</label>
                        <input type="text" value={form.partnerStaffName} onChange={(e) => setForm({ ...form, partnerStaffName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">담당자 연락처</label>
                        <input type="tel" value={form.partnerStaffPhone} onChange={(e) => setForm({ ...form, partnerStaffPhone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="010-0000-0000" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">업체 서비스 내역</label>
                        <textarea value={form.partnerServiceNote} onChange={(e) => setForm({ ...form, partnerServiceNote: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {/* 해외고객 비고 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">해외고객 비고</label>
                  <textarea value={form.overseasNote} onChange={(e) => setForm({ ...form, overseasNote: e.target.value })}
                    rows={2}
                    placeholder="해외 거주/일정 협의 관련 메모"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                </>)}

                {activeTab === 'shooting' && (<>
                {/* 촬영 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-camera-line text-gray-400"></i> 촬영 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">촬영 범위</label>
                      <select value={form.customerType} onChange={(e) => setForm({ ...form, customerType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {CUSTOMER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예식장소 (= 본식장소)</label>
                      <input type="text" value={form.weddingVenue} onChange={(e) => setForm({ ...form, weddingVenue: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    {form.customerType !== 'MAIN' && (
                    <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설 촬영자 1</label>
                      <select value={form.rehearsalPhotographerIdx || ''} onChange={(e) => setForm({ ...form, rehearsalPhotographerIdx: e.target.value ? Number(e.target.value) : '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {staffList.map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설 촬영자 2</label>
                      <select value={form.rehearsalPhotographer2Idx || ''} onChange={(e) => setForm({ ...form, rehearsalPhotographer2Idx: e.target.value ? Number(e.target.value) : '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택 (없으면 비워둠)</option>
                        {staffList.map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                      </select>
                    </div>
                    </>
                    )}
                    {form.customerType !== 'REHEARSAL' && (
                    <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식 촬영자 1</label>
                      <select value={form.mainPhotographerIdx || ''} onChange={(e) => setForm({ ...form, mainPhotographerIdx: e.target.value ? Number(e.target.value) : '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {staffList.map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식 촬영자 2</label>
                      <select value={form.mainPhotographer2Idx || ''} onChange={(e) => setForm({ ...form, mainPhotographer2Idx: e.target.value ? Number(e.target.value) : '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택 (없으면 비워둠)</option>
                        {staffList.map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                      </select>
                    </div>
                    </>
                    )}
                  </div>
                </div>

                {/* 본식 메타 (메이크업샵 / 본식 비고) */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-palette-line text-gray-400"></i> 본식 메타
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">메이크업샵</label>
                      <input type="text" value={form.makeupShopName} onChange={(e) => setForm({ ...form, makeupShopName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">메이크업샵 시간</label>
                      <input type="datetime-local" value={form.makeupShopDt} onChange={(e) => setForm({ ...form, makeupShopDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식 비고</label>
                      <textarea value={form.mainMemo} onChange={(e) => setForm({ ...form, mainMemo: e.target.value })} rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">발주(계약)일</label>
                      <input type="date" value={form.orderDt}
                        onChange={(e) => setForm({ ...form, orderDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
                {/* 일정 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-calendar-heart-line text-gray-400"></i> 일정 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {form.customerType !== 'MAIN' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">리허설 촬영일</label>
                      <input type="datetime-local" value={form.rehearsalDt} onChange={(e) => setForm({ ...form, rehearsalDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    )}
                    {form.customerType !== 'REHEARSAL' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">본식 촬영일</label>
                      <input type="datetime-local" value={form.mainShootingDt} onChange={(e) => setForm({ ...form, mainShootingDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">셀렉일</label>
                      <input type="datetime-local" value={form.selectDt} onChange={(e) => setForm({ ...form, selectDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예식일</label>
                      <input type="datetime-local" value={form.weddingDt} onChange={(e) => setForm({ ...form, weddingDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
                </>)}

                {activeTab === 'selection' && (<>
                {/* 셀렉 정보 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-image-2-line text-gray-400"></i> 셀렉 정보
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">원본 CD 출고일</label>
                      <input type="date" value={form.originalCdShipDt} onChange={(e) => setForm({ ...form, originalCdShipDt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">출고 구분</label>
                      <select value={form.shipMethod}
                        onChange={(e) => setForm({ ...form, shipMethod: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        <option value="PICKUP">방문</option>
                        <option value="COURIER">택배</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">택배 구분</label>
                      <select value={form.courierPayment}
                        onChange={(e) => setForm({ ...form, courierPayment: e.target.value as any })}
                        disabled={form.shipMethod !== 'COURIER'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400">
                        <option value="">선택</option>
                        <option value="PREPAID">선불</option>
                        <option value="COLLECT">착불</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">셀렉 구분</label>
                      <select value={form.selectMethod}
                        onChange={(e) => setForm({ ...form, selectMethod: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        <option value="VISIT">방문셀렉</option>
                        <option value="EMAIL">이메일셀렉</option>
                        <option value="AUTO">임의셀렉</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">셀렉 내용</label>
                      <select value={form.selectContent}
                        onChange={(e) => setForm({ ...form, selectContent: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        <option value="DIRECT">직접</option>
                        <option value="AUTO">임의</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">셀렉 담당자</label>
                      <select value={form.selectManagerIdx || ''}
                        onChange={(e) => setForm({ ...form, selectManagerIdx: e.target.value ? Number(e.target.value) : '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {staffList.map((s: any) => <option key={s.userIdx} value={s.userIdx}>{s.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">셀렉 비고</label>
                      <textarea value={form.selectMemo} onChange={(e) => setForm({ ...form, selectMemo: e.target.value })} rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                    </div>
                  </div>
                </div>
                </>)}

                {activeTab === 'shooting' && (<>
                {/* 알림 설정 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-notification-3-line text-gray-400"></i> 촬영일 알림 설정
                  </h3>
                  <p className="text-xs text-gray-400 mb-3 -mt-2">체크하면 해당 시점에 고객에게 자동으로 안내 문자가 발송됩니다</p>
                  <div className="space-y-3">
                    {([
                      ...(form.customerType !== 'MAIN' ? [{ key: 'rehearsalReminder' as const, label: '리허설 촬영일', dateFn: () => form.rehearsalDt }] : []),
                      ...(form.customerType !== 'REHEARSAL' ? [{ key: 'mainShootingReminder' as const, label: '본식 촬영일', dateFn: () => form.mainShootingDt }] : []),
                      { key: 'selectReminder' as const, label: '셀렉일', dateFn: () => form.selectDt },
                    ]).map(item => (
                      <div key={item.key} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 w-24">{item.label}</span>
                        {(['oneMonth', 'oneWeek', 'oneDay'] as const).map(period => (
                          <label key={period} className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox"
                              checked={form.notificationSettings?.[item.key]?.[period] || false}
                              disabled={!item.dateFn()}
                              onChange={(e) => setForm({
                                ...form,
                                notificationSettings: {
                                  ...form.notificationSettings,
                                  [item.key]: { ...form.notificationSettings?.[item.key], [period]: e.target.checked },
                                },
                              })}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:opacity-40" />
                            <span className={`text-xs ${item.dateFn() ? 'text-gray-600' : 'text-gray-400'}`}>
                              {period === 'oneMonth' ? '1달 전' : period === 'oneWeek' ? '1주 전' : '1일 전'}
                            </span>
                          </label>
                        ))}
                        {!item.dateFn() && <span className="text-[10px] text-gray-400 ml-auto">날짜를 먼저 설정해주세요</span>}
                      </div>
                    ))}
                  </div>
                </div>
                </>)}

                {activeTab === 'basic' && (<>
                {/* 유입 경로 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="ri-route-line text-gray-400"></i> 유입 경로
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입경로</label>
                      <select value={form.referralSource}
                        onChange={(e) => setForm({ ...form, referralSource: e.target.value, referralSourceDetail: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">선택</option>
                        {REFERRAL_SOURCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">유입경로 상세</label>
                      {referralDetailOptions.length > 0 ? (
                        <select value={form.referralSourceDetail}
                          onChange={(e) => setForm({ ...form, referralSourceDetail: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">선택</option>
                          {referralDetailOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                      ) : (
                        <input type="text" value={form.referralSourceDetail}
                          onChange={(e) => setForm({ ...form, referralSourceDetail: e.target.value })}
                          placeholder={form.referralSource ? '직접 입력' : '유입경로를 먼저 선택하세요'}
                          disabled={!form.referralSource}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                </>)}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => { setShowAddModal(false); setEditingRowIdx(null); setForm(initialForm); }}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer">취소</button>
                  <button type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">
                    {editingRowIdx !== null ? '변경 적용' : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 엑셀 다건 매핑 프리뷰 모달 */}
        {importPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">엑셀 매핑 결과 미리보기</h2>
                  <p className="text-xs text-gray-500 mt-1">{importPreview.customers.length}건 감지됨 — 저장할 행을 선택하고 내용을 검토하세요</p>
                </div>
                <button onClick={() => { if (!importProgress) { setImportPreview(null); setImportSelection([]); } }}
                  disabled={!!importProgress}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-40">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {importPreview.globalWarnings.length > 0 && (
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-xs text-amber-800">
                  {importPreview.globalWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
                  <input type="checkbox"
                    checked={importSelection.every(Boolean)}
                    onChange={(e) => setImportSelection(importPreview.customers.map(() => e.target.checked))}
                    className="w-4 h-4 cursor-pointer" />
                  <span className="text-sm text-gray-600">전체 선택</span>
                  <span className="ml-auto text-xs text-gray-400">선택: {importSelection.filter(Boolean).length} / {importPreview.customers.length}</span>
                </div>

                {importPreview.customers.map((c, i) => {
                  const b = c.basic || {};
                  const s = c.shooting || {};
                  const sel = c.selection || {};
                  const typeLabel = s.customerType === 'REHEARSAL_AND_MAIN' ? '리허설+본식' : s.customerType === 'REHEARSAL' ? '리허설' : s.customerType === 'MAIN' ? '본식' : '-';
                  const channelLabel = b.customerChannel === 'VENDOR' ? '업체고객' : b.customerChannel === 'WALKING' ? '워킹고객' : null;
                  const shipLabel = sel.shipMethod === 'PICKUP' ? '방문' : sel.shipMethod === 'COURIER' ? '택배' : null;
                  const selectMethodLabel = sel.selectMethod === 'VISIT' ? '방문셀렉' : sel.selectMethod === 'EMAIL' ? '이메일셀렉' : sel.selectMethod === 'AUTO' ? '임의셀렉' : null;
                  const renderField = (label: string, value: any) => value ? (
                    <div className="flex gap-1"><span className="text-gray-400">{label}</span><span className="text-gray-700 font-medium truncate">{value}</span></div>
                  ) : null;
                  const fmtDt = (dt?: string | null) => dt ? dt.slice(0, 16).replace('T', ' ') : null;
                  return (
                    <div key={i} className={`border rounded-lg ${importSelection[i] ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
                      {/* 헤더 */}
                      <div className="flex items-center gap-3 p-3 border-b border-gray-100">
                        <input type="checkbox" checked={importSelection[i] || false}
                          onChange={(e) => { const next = [...importSelection]; next[i] = e.target.checked; setImportSelection(next); }}
                          className="w-4 h-4 cursor-pointer" />
                        <span className="text-sm font-semibold text-gray-700">#{i + 1}</span>
                        <span className="text-sm text-gray-800 font-medium">
                          {b.groomName || '-'} / {b.brideName || '-'}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{typeLabel}</span>
                        {channelLabel && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{channelLabel}</span>}
                        <button type="button" onClick={() => handleEditPreviewRow(i)}
                          className="ml-auto text-xs px-3 py-1 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 cursor-pointer font-medium flex items-center gap-1">
                          <i className="ri-edit-line"></i> 편집
                        </button>
                      </div>

                      {/* 세부 매핑 */}
                      <div className="px-3 py-2.5 grid grid-cols-3 gap-x-4 gap-y-1 text-[11px]">
                        {/* 기본정보 */}
                        <div className="col-span-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide pt-1">기본정보</div>
                        {renderField('신랑', b.groomName ? `${b.groomName} ${b.groomPhone ? `(${b.groomPhone})` : ''}` : null)}
                        {renderField('신부', b.brideName ? `${b.brideName} ${b.bridePhone ? `(${b.bridePhone})` : ''}` : null)}
                        {renderField('신랑이메일', b.groomEmail)}
                        {renderField('신부이메일', b.brideEmail)}
                        {renderField('업체명', b.partnerVendorName)}
                        {renderField('업체담당', b.partnerStaffName && (b.partnerStaffPhone ? `${b.partnerStaffName} (${b.partnerStaffPhone})` : b.partnerStaffName))}
                        {renderField('유입경로', b.referralSource)}
                        {renderField('메모', b.memo)}
                        {renderField('해외비고', b.overseasNote)}

                        {/* 일정·촬영 */}
                        <div className="col-span-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide pt-2">일정·촬영</div>
                        {renderField('예식장', s.weddingVenue)}
                        {renderField('예식일', fmtDt(s.weddingDt))}
                        {renderField('리허설', fmtDt(s.rehearsalDt))}
                        {renderField('본식촬영', fmtDt(s.mainShootingDt))}
                        {renderField('셀렉일', fmtDt(s.selectDt))}
                        {renderField('발주일', s.orderDt?.slice(0, 10))}
                        {renderField('메이크업샵', s.makeupShopName)}
                        {renderField('메이크업시간', fmtDt(s.makeupShopDt))}
                        {renderField('본식비고', s.mainMemo)}

                        {/* 셀렉·출고 */}
                        <div className="col-span-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide pt-2">셀렉·출고</div>
                        {renderField('출고구분', shipLabel)}
                        {renderField('택배구분', sel.courierPayment === 'PREPAID' ? '선불' : sel.courierPayment === 'COLLECT' ? '착불' : null)}
                        {renderField('셀렉구분', selectMethodLabel)}
                        {renderField('셀렉내용', sel.selectContent === 'DIRECT' ? '직접' : sel.selectContent === 'AUTO' ? '임의' : null)}
                        {renderField('CD출고일', sel.originalCdShipDt?.slice(0, 10))}
                        {renderField('셀렉비고', sel.selectMemo)}
                      </div>

                      {c.warnings?.length > 0 && (
                        <div className="border-t border-amber-100 bg-amber-50/50 px-3 py-2 text-[11px] text-amber-700 space-y-0.5">
                          {c.warnings.map((w: string, j: number) => <div key={j}>⚠ {w}</div>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                {importProgress ? (
                  <div className="text-sm text-gray-600">
                    저장 중... {importProgress.done} / {importProgress.total}
                    {importProgress.failed.length > 0 && <span className="ml-3 text-red-600">(실패 {importProgress.failed.length})</span>}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">💡 촬영자와 셀렉 담당자는 매핑되지 않습니다 — 등록 후 고객 상세에서 수동 배정해주세요</div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => { setImportPreview(null); setImportSelection([]); }}
                    disabled={!!importProgress}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium cursor-pointer disabled:opacity-40">취소</button>
                  <button onClick={handleBulkCreateFromPreview}
                    disabled={!!importProgress || importSelection.filter(Boolean).length === 0}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer disabled:opacity-40">
                    선택 {importSelection.filter(Boolean).length}건 등록
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
